import { useMemo } from 'react';
import { useI18n } from '../../../i18n/useI18n';
import type { KnowledgeBaseDocumentTenantAnalyticsDTO } from '../../../types/lac';
import { tenantAnalyticsDisplayName } from '../knowledgeBaseDocumentAnalyticsUtils';
import { ALL_TENANTS_FILTER } from '../lacDocumentsListParams';
import { useLacKnowledgeBaseDocumentAnalyticsQuery } from '../queries/useLacKnowledgeBaseDocumentAnalyticsQuery';
import { useLacTenantsQuery } from '../queries/useLacTenantsQuery';
import { useLacDocumentsSearchParams } from './useLacDocumentsSearchParams';

type UseLacDocumentsTabOptions = {
  tenantName?: string;
};

export function useLacDocumentsTab({
  tenantName,
}: UseLacDocumentsTabOptions = {}) {
  const { locale, translate } = useI18n();
  const scopedTenantName = tenantName?.trim() || undefined;
  const showTenantFilter = scopedTenantName == null;
  const tenantsQuery = useLacTenantsQuery();
  const filters = useLacDocumentsSearchParams({
    showTenantFilter,
    tenants: tenantsQuery.data,
  });

  const filteredTenantName =
    scopedTenantName ??
    (filters.tenantFilter !== ALL_TENANTS_FILTER
      ? filters.tenantFilter
      : undefined);

  const analyticsQuery = useLacKnowledgeBaseDocumentAnalyticsQuery(
    filteredTenantName,
    filters.dateRange,
  );

  const rows = useMemo(() => {
    const data = [...(analyticsQuery.data ?? [])].filter(
      (
        row,
      ): row is KnowledgeBaseDocumentTenantAnalyticsDTO & {
        tenantName: string;
      } => Boolean(row.tenantName?.trim()),
    );

    return data.sort((left, right) => {
      const byCount = (right.documentCount ?? 0) - (left.documentCount ?? 0);
      if (byCount !== 0) {
        return byCount;
      }
      return tenantAnalyticsDisplayName(left).localeCompare(
        tenantAnalyticsDisplayName(right),
        locale,
        { sensitivity: 'base' },
      );
    });
  }, [analyticsQuery.data, locale]);

  const fileSizeLabels = {
    min: translate('lac.documents.fileSize.smallest'),
    avg: translate('lac.documents.fileSize.average'),
    max: translate('lac.documents.fileSize.largest'),
  };
  const emptyValue = translate('lac.documents.emptyValue');

  const isEmpty =
    !analyticsQuery.isLoading && !analyticsQuery.isError && rows.length === 0;

  return {
    translate,
    showTenantFilter,
    tenantsQuery,
    filters,
    analyticsQuery,
    rows,
    fileSizeLabels,
    emptyValue,
    isEmpty,
  };
}
